import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Table, Typography, Card, Avatar, Tag } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { all_routes } from '../../../router/all_routes';
import { ForgottenCheckinDto } from '../../../types/user.types';
import { handleApiError } from '../../../utils/errorHandler';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import FooterSection from '../../shared/components/footerSection';
import { getFileUrl } from '../../../utils/file.utils';
import { useTranslation } from 'react-i18next';
import userService from '../../../services/user/user.service';

dayjs.locale('tr');
dayjs.extend(localizedFormat);

const { Title, Text } = Typography;

const ForgottenCheckIn: React.FC = () => {
  const { t } = useTranslation();
  const [forgottenRecords, setForgottenRecords] = useState<ForgottenCheckinDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchForgottenCheckIns = useCallback(async (checkTime?: string) => {
    setLoading(true);
    setError(null);
    try {
      
      const data = await userService.getForgottenCheckins(checkTime);
      setForgottenRecords(data || []);
    } catch (err: any) {
      handleApiError(err, t);
      setError(t('forgottenCheckIns.errors.fetchError', "Giriş yapmayı unutan çalışan verileri alınırken bir hata oluştu."));
      setForgottenRecords([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const checkTime = dayjs().format('YYYY-MM-DDTHH:mm:ss');
    fetchForgottenCheckIns(checkTime);
  }, [fetchForgottenCheckIns]);
  
  const columns = [
    {
      title: t('forgottenCheckIns.table.employee', "Çalışan"),
      key: 'employee',
      render: (record: ForgottenCheckinDto) => (
        <div className="d-flex align-items-center">
          <Avatar 
            src={record?.profilePicture ? getFileUrl(record.profilePicture) : record?.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'} 
            size="large" 
            className="me-3" 
          />
          <div>
            <Link to={record?.id ? all_routes.employeedetails.replace(':id', record.id.toString()) : '#'}>
              <Title level={5} style={{ marginBottom: 0 }}>{record?.firstName} {record?.lastName}</Title>
            </Link>
            <Text type="secondary" style={{fontSize: '13px'}}>
              {record?.title || '-'}
            </Text>
            <br/>
            <Text type="secondary" style={{fontSize: '12px'}}>
              {record?.department && record.department.length > 0 
                ? record.department.map((dept) => dept.name).join(', ')
                : '-'
              }
            </Text>
            <br/>
            <Text type="secondary" style={{fontSize: '12px'}}>
              {record?.branch && record.branch.length > 0 
                ? record.branch.map((br) => br.name).join(', ')
                : '-'
              }
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('forgottenCheckIns.table.shiftStart', "Vardiya Başlangıcı"),
      dataIndex: 'shiftStart',
      key: 'shiftStart',
      render: (text: string) => (
        <div className="text-center">
          <Tag color="blue">{text || '-'}</Tag>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Breadcrumb */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <Title level={3} className="page-title">
                {t('forgottenCheckIns.pageTitle', "Giriş yapmayı unutan çalışanlar")}
              </Title>
            </div>
          </div>
        </div>

        <Card bordered={false} style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div className="card-body">
            {loading ? (
              <LoadingSpinner centered />
            ) : error ? (
              <div className="text-center py-4">
                <Text type="danger">{error}</Text>
              </div>
            ) : forgottenRecords.length === 0 ? (
              <div className="text-center py-4">
                <ClockCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#52c41a' }}>
                  {t('forgottenCheckIns.noRecords.title', "Harika! Tüm çalışanlar giriş yaptı")}
                </Title>
                <Text type="secondary">
                  {t('forgottenCheckIns.noRecords.description', "Giriş yapmayı unutan çalışan bulunmuyor.")}
                </Text>
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={forgottenRecords}
                rowKey={(record) => `${record.id}`}
                pagination={{ 
                  pageSize: 10, 
                  showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ${t('common.table.records', 'kayıt')}` 
                }}
                className="table-hover"
                scroll={{ x: 1200 }}
              />
            )}
          </div>
        </Card>
      </div>
      
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 mt-auto">
        <FooterSection />
      </div>
    </div>
  );
};

export default ForgottenCheckIn;
